
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authStorage } from "./authStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => authStorage.getToken());
  const [user, setUser] = useState(() => authStorage.getUser());

  const refresh = useCallback(() => {
    setToken(authStorage.getToken());
    setUser(authStorage.getUser());
  }, []);

  useEffect(() => {
    window.addEventListener("ecogynize:auth-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("ecogynize:auth-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  const setSession = useCallback((session) => {
    authStorage.setSession(session);
    refresh();
  }, [refresh]);

  const updateUser = useCallback((patch) => {
    const nextUser = authStorage.updateUser(patch);
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(() => {
    authStorage.clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    token,
    user,
    isAuthenticated: Boolean(token),
    setSession,
    updateUser,
    logout,
  }), [logout, setSession, token, updateUser, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
